import { FC, useCallback, useEffect, useState } from 'react'
import { useTranslation } from 'next-i18next'
import FormInputSearch from 'shared/components/form-input-search'
import { Loader } from 'shared/components/loader'
import { useDebouncedValue } from 'shared/hooks/use-debounced-value'
import { useInViewport } from 'shared/hooks/use-in-viewport'
import { GIFObject } from 'modules/attachments/api/tenor.types'
import { useGifs } from 'modules/attachments/api/use-gifs'

interface GifPickerPanelProps {
  onSelect: (gif: GIFObject) => void
  onClose: () => void
}

export const GifPickerPanel: FC<GifPickerPanelProps> = ({ onSelect, onClose }) => {
  const { t } = useTranslation()

  const [query, setQuery] = useState('')
  const [debouncedQuery] = useDebouncedValue(query, 300)
  const { gifs, error, isLoadingMore, isReachingEnd, loadMore } = useGifs(debouncedQuery, 16)

  const { ref, inViewport } = useInViewport()

  useEffect(() => {
    if (inViewport && !isReachingEnd && !isLoadingMore) {
      loadMore()
    }
  }, [inViewport, isReachingEnd, isLoadingMore])

  const handleQueryChange = useCallback((value: string) => {
    setQuery(value)
  }, [])

  const handleGifSelect = (gif: GIFObject): void => {
    onSelect(gif)
    onClose()
  }

  return (
    <div className="relative z-10 flex max-h-[875px] w-72 flex-col overflow-auto rounded-xl bg-background-secondary shadow-2xl">
      <div className="h-96 w-72 max-w-full pb-11">
        <div className="relative flex size-full flex-col items-center overflow-auto p-4">
          <FormInputSearch
            className="relative mb-4 flex h-10 w-full items-center justify-start rounded-md border border-text-secondary/30 focus-within:border-brand-primary"
            inputClassName="h-full w-full border-none bg-transparent outline-hidden"
            placeholder={t('global.search')}
            value={query}
            onChange={e => handleQueryChange(e.target.value)}
            onClear={() => handleQueryChange('')}
          />

          <div className="flex w-full flex-wrap justify-between">
            {gifs.map(gif => (
              <div
                key={gif.id}
                className="mb-2 h-[77px] w-[calc(50%-4px)] animate-loadingGifPulse cursor-pointer overflow-hidden rounded-sm bg-size-[400px]"
                onClick={() => handleGifSelect(gif)}
              >
                <div
                  className="size-full bg-cover bg-center"
                  style={{ backgroundImage: `url(${gif.tinyUrl})` }}
                />
              </div>
            ))}
          </div>

          <div ref={ref} className="w-full" />

          {isLoadingMore && (
            <div className="flex items-center justify-center p-5">
              <Loader type="light" className="fill-transparent" />.
            </div>
          )}
          {isReachingEnd && gifs.length > 0 && (
            <div className="py-4 text-sm text-text-tertiary">{t('gifs.panel.end_of_results')}</div>
          )}
          {gifs.length === 0 && !isLoadingMore && !error && (
            <div className="py-4 text-sm text-text-tertiary">{t('gifs.panel.nothing_found')}</div>
          )}
        </div>
        <div className="absolute bottom-0 left-0 flex h-11 w-full items-center bg-background-secondary" />
      </div>
    </div>
  )
}
